// src/components/marketing/ContactSection.js
// Kontaktformular mit Brevo Transaktionsmail an wedding@sarahiver.de
// Spam-Schutz: hCaptcha + Honeypot + Zeitprüfung
// KEIN Double Opt-In nötig (Kontaktanfrage ≠ Newsletter)
import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { PUBLIC_PACKAGES } from '../../lib/pricing';
import { getStyleChoice } from './demoData';
import { useTheme } from '../../context/ThemeContext';
import usePartnerRef from '../../hooks/usePartnerRef';
import { trackFormSubmit, trackFormStart, trackInquirySubmit } from '../../utils/analytics';

// ============================================
// THEME CONFIGURATIONS
// ============================================
const THEME_CONFIG = {
  editorial: {
    bg: '#0A0A0A',
    cardBg: 'transparent',
    text: '#FAFAFA',
    textMuted: 'rgba(255,255,255,0.6)',
    accent: '#C41E3A',
    inputBg: 'transparent',
    inputBorder: 'rgba(255,255,255,0.2)',
    inputFocusBorder: '#C41E3A',
    buttonBg: '#C41E3A',
    buttonText: '#FAFAFA',
    buttonHoverBg: '#a01830',
    headlineFont: "'Oswald', sans-serif",
    bodyFont: "'Inter', sans-serif",
    accentFont: "'Source Serif 4', serif",
    labelStyle: 'uppercase',
    inputStyle: 'border',
    borderRadius: '0',
  },
  botanical: {
    bg: '#040604',
    cardBg: 'rgba(255,255,255,0.06)',
    cardBlur: '40px',
    cardBorder: 'rgba(255,255,255,0.1)',
    text: 'rgba(255,255,255,0.95)',
    textMuted: 'rgba(255,255,255,0.5)',
    accent: 'rgba(255,255,255,0.95)',
    inputBg: 'rgba(255,255,255,0.05)',
    inputBorder: 'rgba(255,255,255,0.1)',
    inputFocusBorder: 'rgba(255,255,255,0.3)',
    buttonBg: 'rgba(255,255,255,0.95)',
    buttonText: '#040604',
    buttonHoverBg: '#fff',
    headlineFont: "'Cormorant Garamond', serif",
    bodyFont: "'Montserrat', sans-serif",
    labelStyle: 'uppercase',
    inputStyle: 'rounded',
    borderRadius: '12px',
    cardRadius: '24px',
  },
  contemporary: {
    bg: '#FFE66D',
    cardBg: '#FFFFFF',
    cardBorder: '#0D0D0D',
    cardShadow: '8px 8px 0 #0D0D0D',
    text: '#0D0D0D',
    textMuted: '#525252',
    accent: '#FF6B6B',
    inputBg: '#FAFAFA',
    inputBorder: '#0D0D0D',
    inputFocusBorder: '#0D0D0D',
    inputFocusShadow: '4px 4px 0 #4ECDC4',
    buttonBg: '#FF6B6B',
    buttonText: '#FAFAFA',
    buttonBorder: '#0D0D0D',
    buttonShadow: '6px 6px 0 #0D0D0D',
    buttonHoverTransform: 'translate(-3px, -3px)',
    buttonHoverShadow: '9px 9px 0 #0D0D0D',
    headlineFont: "'Space Grotesk', sans-serif",
    bodyFont: "'Space Grotesk', sans-serif",
    labelStyle: 'uppercase',
    inputStyle: 'brutalist',
    borderRadius: '0',
  },
  luxe: {
    bg: '#0A0A0A',
    cardBg: 'transparent',
    text: '#F8F6F3',
    textMuted: 'rgba(248,246,243,0.6)',
    accent: '#C9A962',
    inputBg: 'transparent',
    inputBorder: 'rgba(248,246,243,0.2)',
    inputFocusBorder: '#C9A962',
    buttonBg: '#C9A962',
    buttonText: '#0A0A0A',
    buttonHoverBg: '#d4b66f',
    headlineFont: "'Cormorant', serif",
    bodyFont: "'Outfit', sans-serif",
    labelStyle: 'uppercase',
    inputStyle: 'underline',
    borderRadius: '0',
  },
  neon: {
    bg: '#0a0a0f',
    cardBg: 'rgba(255,255,255,0.02)',
    cardBorder: 'rgba(0,255,255,0.2)',
    text: '#FFFFFF',
    textMuted: 'rgba(255,255,255,0.5)',
    accent: '#00ffff',
    accentAlt: '#ff00ff',
    inputBg: 'rgba(255,255,255,0.02)',
    inputBorder: 'rgba(0,255,255,0.2)',
    inputFocusBorder: '#00ffff',
    inputFocusShadow: '0 0 15px rgba(0,255,255,0.2)',
    buttonBg: 'transparent',
    buttonText: '#00ffff',
    buttonBorder: '#00ffff',
    buttonShadow: '0 0 15px rgba(0,255,255,0.3)',
    buttonHoverBg: 'rgba(0,255,255,0.1)',
    buttonHoverShadow: '0 0 25px rgba(0,255,255,0.5)',
    headlineFont: "'Space Grotesk', sans-serif",
    bodyFont: "'Space Grotesk', sans-serif",
    labelStyle: 'uppercase',
    inputStyle: 'glow',
    borderRadius: '0',
  },
  video: {
    bg: '#0A0A0A',
    cardBg: 'transparent',
    text: '#FFFFFF',
    textMuted: '#B0B0B0',
    accent: '#6B8CAE',
    inputBg: 'transparent',
    inputBorder: 'rgba(255,255,255,0.2)',
    inputFocusBorder: '#6B8CAE',
    buttonBg: '#6B8CAE',
    buttonText: '#FFFFFF',
    buttonHoverBg: '#7d9cba',
    headlineFont: "'Manrope', sans-serif",
    bodyFont: "'Inter', sans-serif",
    accentFont: "'Cormorant Garamond', serif",
    labelStyle: 'uppercase',
    inputStyle: 'underline',
    borderRadius: '0',
  },
  classic: {
    bg: '#FFFFFF',
    cardBg: 'transparent',
    text: '#1A1A1A',
    textMuted: '#999999',
    accent: '#999999',
    inputBg: 'transparent',
    inputBorder: 'rgba(0,0,0,0.1)',
    inputFocusBorder: '#999999',
    buttonBg: '#1A1A1A',
    buttonText: '#FFFFFF',
    buttonHoverBg: '#333333',
    headlineFont: "'Cormorant Garamond', serif",
    bodyFont: "'Josefin Sans', sans-serif",
    accentFont: "'Mrs Saint Delafield', cursive",
    labelStyle: 'uppercase',
    inputStyle: 'border',
    borderRadius: '0',
  },
};

// ============================================
// STYLED COMPONENTS
// ============================================
const Section = styled.section`
  padding: clamp(4rem, 10vh, 8rem) clamp(1.5rem, 5vw, 4rem);
  background: ${p => p.$config.bg};
  position: relative;
  
  ${p => p.$theme === 'botanical' && css`
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(ellipse at 50% 100%, rgba(45, 90, 60, 0.1) 0%, transparent 50%);
    }
  `}
  
  ${p => p.$theme === 'neon' && css`
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(ellipse at 50% 50%, rgba(0, 255, 255, 0.03) 0%, transparent 50%);
    }
  `}
`;

const Container = styled.div`
  max-width: 700px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Eyebrow = styled.p`
  font-family: ${p => p.$config.bodyFont};
  font-size: ${p => p.$theme === 'botanical' ? '0.6rem' : p.$theme === 'luxe' ? '0.7rem' : '0.75rem'};
  font-weight: ${p => p.$theme === 'contemporary' ? '700' : p.$theme === 'luxe' ? '400' : '600'};
  letter-spacing: ${p => p.$theme === 'botanical' || p.$theme === 'luxe' ? '0.4em' : '0.2em'};
  text-transform: uppercase;
  color: ${p => p.$config.accent};
  margin-bottom: 1rem;
  
  ${p => p.$theme === 'neon' && css`
    text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
  `}
`;

const Title = styled.h2`
  font-family: ${p => p.$config.headlineFont};
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: ${p => ['botanical', 'luxe', 'classic'].includes(p.$theme) ? '300' : '700'};
  font-style: ${p => p.$theme === 'luxe' ? 'italic' : 'normal'};
  text-transform: ${p => ['editorial', 'contemporary', 'neon'].includes(p.$theme) ? 'uppercase' : 'none'};
  color: ${p => p.$config.text};
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  font-family: ${p => p.$config.accentFont || p.$config.bodyFont};
  font-size: ${p => p.$theme === 'video' || p.$theme === 'editorial' ? '1.1rem' : '0.9rem'};
  font-style: ${p => ['editorial', 'video'].includes(p.$theme) ? 'italic' : 'normal'};
  color: ${p => p.$config.textMuted};
  max-width: 500px;
  margin: 0 auto;
`;

const FormCard = styled.div`
  background: ${p => p.$config.cardBg};
  border-radius: ${p => p.$config.cardRadius || p.$config.borderRadius};
  
  ${p => p.$config.cardBlur && css`
    backdrop-filter: blur(${p.$config.cardBlur});
    -webkit-backdrop-filter: blur(${p.$config.cardBlur});
  `}
  
  ${p => p.$config.cardBorder && css`
    border: ${p.$theme === 'contemporary' ? '3px' : '1px'} solid ${p.$config.cardBorder};
  `}
  
  ${p => p.$config.cardShadow && css`
    box-shadow: ${p.$config.cardShadow};
  `}
  
  ${p => (p.$theme === 'botanical' || p.$theme === 'contemporary' || p.$theme === 'neon') && css`
    padding: 2.5rem;
  `}
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  
  @media (min-width: 500px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  font-family: ${p => p.$config.bodyFont};
  font-size: ${p => p.$theme === 'botanical' ? '0.65rem' : p.$theme === 'luxe' ? '0.65rem' : '0.75rem'};
  font-weight: ${p => p.$theme === 'contemporary' ? '700' : p.$theme === 'luxe' ? '400' : '600'};
  letter-spacing: ${p => p.$theme === 'luxe' ? '0.2em' : p.$theme === 'botanical' ? '0.15em' : '0.1em'};
  text-transform: ${p => p.$config.labelStyle === 'uppercase' ? 'uppercase' : 'none'};
  color: ${p => p.$theme === 'neon' ? p.$config.accentAlt : p.$config.textMuted};
  margin-bottom: 0.5rem;
`;

const inputBaseStyles = css`
  width: 100%;
  padding: 1rem;
  font-family: ${p => p.$config.bodyFont};
  font-size: 1rem;
  color: ${p => p.$config.text};
  background: ${p => p.$config.inputBg};
  transition: all 0.3s ease;
  
  &::placeholder {
    color: ${p => p.$config.textMuted};
    opacity: 0.5;
  }
  
  &:focus {
    outline: none;
  }
`;

const Input = styled.input`
  ${inputBaseStyles}
  border-radius: ${p => p.$config.borderRadius};
  
  ${p => p.$config.inputStyle === 'border' && css`
    border: 1px solid ${p.$config.inputBorder};
    &:focus { border-color: ${p.$config.inputFocusBorder}; }
  `}
  
  ${p => p.$config.inputStyle === 'rounded' && css`
    border: 1px solid ${p.$config.inputBorder};
    border-radius: 12px;
    &:focus { 
      border-color: ${p.$config.inputFocusBorder}; 
      background: rgba(255,255,255,0.08);
    }
  `}
  
  ${p => p.$config.inputStyle === 'brutalist' && css`
    border: 2px solid ${p.$config.inputBorder};
    &:focus { box-shadow: ${p.$config.inputFocusShadow}; }
  `}
  
  ${p => p.$config.inputStyle === 'underline' && css`
    border: none;
    border-bottom: 1px solid ${p.$config.inputBorder};
    border-radius: 0;
    padding-left: 0;
    padding-right: 0;
    &:focus { border-color: ${p.$config.inputFocusBorder}; }
  `}
  
  ${p => p.$config.inputStyle === 'glow' && css`
    border: 1px solid ${p.$config.inputBorder};
    &:focus { 
      border-color: ${p.$config.inputFocusBorder}; 
      box-shadow: ${p.$config.inputFocusShadow};
    }
  `}
`;

const Textarea = styled.textarea`
  ${inputBaseStyles}
  min-height: 150px;
  resize: vertical;
  border-radius: ${p => p.$config.borderRadius};
  
  ${p => p.$config.inputStyle === 'border' && css`
    border: 1px solid ${p.$config.inputBorder};
    &:focus { border-color: ${p.$config.inputFocusBorder}; }
  `}
  
  ${p => p.$config.inputStyle === 'rounded' && css`
    border: 1px solid ${p.$config.inputBorder};
    border-radius: 12px;
    &:focus { 
      border-color: ${p.$config.inputFocusBorder}; 
      background: rgba(255,255,255,0.08);
    }
  `}
  
  ${p => p.$config.inputStyle === 'brutalist' && css`
    border: 2px solid ${p.$config.inputBorder};
    &:focus { box-shadow: ${p.$config.inputFocusShadow}; }
  `}
  
  ${p => p.$config.inputStyle === 'underline' && css`
    border: none;
    border-bottom: 1px solid ${p.$config.inputBorder};
    border-radius: 0;
    padding-left: 0;
    padding-right: 0;
    &:focus { border-color: ${p.$config.inputFocusBorder}; }
  `}
  
  ${p => p.$config.inputStyle === 'glow' && css`
    border: 1px solid ${p.$config.inputBorder};
    &:focus { 
      border-color: ${p.$config.inputFocusBorder}; 
      box-shadow: ${p.$config.inputFocusShadow};
    }
  `}
`;

const Select = styled.select`
  ${inputBaseStyles}
  border-radius: ${p => p.$config.borderRadius};
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23888' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 2.5rem;
  
  ${p => p.$config.inputStyle === 'border' && css`
    border: 1px solid ${p.$config.inputBorder};
    &:focus { border-color: ${p.$config.inputFocusBorder}; }
  `}
  
  ${p => p.$config.inputStyle === 'rounded' && css`
    border: 1px solid ${p.$config.inputBorder};
    border-radius: 12px;
    &:focus { 
      border-color: ${p.$config.inputFocusBorder}; 
      background-color: rgba(255,255,255,0.08);
    }
  `}
  
  ${p => p.$config.inputStyle === 'brutalist' && css`
    border: 2px solid ${p.$config.inputBorder};
    &:focus { box-shadow: ${p.$config.inputFocusShadow}; }
  `}
  
  ${p => p.$config.inputStyle === 'underline' && css`
    border: none;
    border-bottom: 1px solid ${p.$config.inputBorder};
    border-radius: 0;
    padding-left: 0;
    &:focus { border-color: ${p.$config.inputFocusBorder}; }
  `}
  
  ${p => p.$config.inputStyle === 'glow' && css`
    border: 1px solid ${p.$config.inputBorder};
    &:focus { 
      border-color: ${p.$config.inputFocusBorder}; 
      box-shadow: ${p.$config.inputFocusShadow};
    }
  `}
  
  option {
    background: #1a1a1a;
    color: #fff;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 1.25rem;
  font-family: ${p => p.$config.headlineFont};
  font-size: ${p => p.$theme === 'luxe' ? '0.7rem' : p.$theme === 'botanical' ? '0.7rem' : '0.9rem'};
  font-weight: ${p => ['editorial', 'contemporary', 'neon'].includes(p.$theme) ? '600' : '500'};

  letter-spacing: ${p => p.$theme === 'luxe' ? '0.25em' : p.$theme === 'botanical' ? '0.15em' : '0.1em'};
  text-transform: uppercase;
  color: ${p => p.$config.buttonText};
  background: ${p => p.$config.buttonBg};
  border: ${p => p.$config.buttonBorder ? `${p.$theme === 'contemporary' ? '3px' : '1px'} solid ${p.$config.buttonBorder}` : 'none'};
  border-radius: ${p => p.$theme === 'botanical' ? '50px' : p.$config.borderRadius};
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${p => p.$config.buttonShadow && css`
    box-shadow: ${p.$config.buttonShadow};
  `}
  
  &:hover {
    background: ${p => p.$config.buttonHoverBg || p.$config.buttonBg};
    ${p => p.$config.buttonHoverTransform && css`transform: ${p.$config.buttonHoverTransform};`}
    ${p => p.$config.buttonHoverShadow && css`box-shadow: ${p.$config.buttonHoverShadow};`}
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

// Honeypot (hidden from users, bots fill it)
const Honeypot = styled.input`
  position: absolute;
  left: -9999px;
  opacity: 0;
  height: 0;
  width: 0;
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  
  h3 {
    font-family: ${p => p.$config.headlineFont};
    font-size: 1.8rem;
    font-weight: ${p => ['botanical', 'luxe'].includes(p.$theme) ? '300' : '700'};
    font-style: ${p => p.$theme === 'luxe' ? 'italic' : 'normal'};
    color: ${p => p.$config.text};
    margin-bottom: 1rem;
  }
  
  p {
    font-family: ${p => p.$config.bodyFont};
    font-size: 1rem;
    color: ${p => p.$config.textMuted};
    line-height: 1.7;
    margin-bottom: 0.5rem;
  }
  
  .highlight {
    color: ${p => p.$config.accent};
    font-weight: 600;
  }
`;

const ErrorMessage = styled.p`
  font-family: ${p => p.$config.bodyFont};
  font-size: 0.85rem;
  color: #FF6B6B;
  margin-top: 0.5rem;
`;

const PrivacyNote = styled.p`
  font-family: ${p => p.$config.bodyFont};
  font-size: 0.75rem;
  color: ${p => p.$config.textMuted};
  text-align: center;
  margin-top: 1rem;
  opacity: 0.7;

  a {
    color: ${p => p.$config.accent};
    text-decoration: underline;
  }
`;

// ============================================
// CLASSIC-SPECIFIC COMPONENTS
// ============================================
const CLASSIC_CONTACT_IMAGE = 'https://res.cloudinary.com/si-weddings/image/upload/q_auto,f_auto,w_800/v1771173517/Contact_akucsg.jpg';

const ClassicSection = styled.section`
  padding: 0;
  background: #FFFFFF;
  position: relative;
`;

const ClassicGrid = styled.div`
  display: grid;
  grid-template-columns: clamp(280px, 38vw, 500px) 1fr;
  min-height: 100vh;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const ClassicImageCol = styled.div`
  position: relative;

  @media (max-width: 900px) {
    height: 300px;
  }
`;

const ClassicImage = styled.img`
  position: sticky;
  top: 0;
  width: 100%;
  height: 100vh;
  object-fit: cover;
  display: block;
  /* filter removed */

  @media (max-width: 900px) {
    position: relative;
    height: 300px;
  }
`;

const ClassicFormCol = styled.div`
  padding: clamp(3rem, 6vh, 6rem) clamp(2rem, 5vw, 5rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ClassicHeader = styled.div`
  margin-bottom: clamp(2rem, 4vh, 3.5rem);
`;

const ClassicEyebrow = styled.p`
  font-family: 'Josefin Sans', sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #999999;
  margin-bottom: 1rem;
`;

const ClassicTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2.2rem, 4vw, 3.2rem);
  font-weight: 300;
  color: #1A1A1A;
  margin-bottom: 0.75rem;
  line-height: 1.15;
`;

const ClassicSubtitle = styled.p`
  font-family: 'Josefin Sans', sans-serif;
  font-size: 0.9rem;
  color: #999999;
  line-height: 1.6;
`;

// ============================================
// API ENDPOINT (Vercel Serverless Function — alle Secrets serverseitig)
// ============================================

// hCaptcha (Site Key ist öffentlich — das ist korrekt so)
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-000000000001'; // Test key as fallback

// ============================================
// hCaptcha STYLED
// ============================================
const OptionalToggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0.25rem 0 0.5rem;
  font-family: inherit;
  font-size: 0.85rem;
  color: ${p => p.$config?.textMuted || '#999'};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  align-self: flex-start;

  &:hover {
    color: ${p => p.$config?.accent || '#1A1A1A'};
  }
`;

const OptionalFields = styled.div`
  display: ${p => p.$open ? 'block' : 'none'};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0 0.25rem;
`;

const Chip = styled.button`
  background: none;
  border: 1px solid ${p => p.$config?.inputBorder || 'rgba(0,0,0,0.2)'};
  border-radius: 99px;
  padding: 0.4rem 0.85rem;
  font-family: inherit;
  font-size: 0.78rem;
  color: ${p => p.$config?.textMuted || '#777'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${p => p.$config?.accent || '#1A1A1A'};
    color: ${p => p.$config?.accent || '#1A1A1A'};
  }
`;

const CaptchaWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0.5rem 0;
  min-height: 78px;
`;

// ============================================
// COMPONENT
// ============================================
// Paketauswahl zentral aus lib/pricing.js
const PACKAGES = [
  { id: '', label: 'Bitte wählen...' },
  ...PUBLIC_PACKAGES.map(pkg => ({
    id: pkg.id,
    label: `${pkg.name} (€${new Intl.NumberFormat('de-DE').format(pkg.price)})`,
  })),
];

const StyleBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
  padding: 0.7rem 1rem;
  border-left: 3px solid #C41E3A;
  background: rgba(196, 30, 58, 0.04);
  font-family: 'Josefin Sans', sans-serif;
  font-size: 0.85rem;
  color: #1A1A1A;

  strong { font-weight: 600; }

  button {
    margin-left: auto;
    background: none;
    border: none;
    font-size: 0.75rem;
    color: #999;
    cursor: pointer;
    text-decoration: underline;
    &:hover { color: #1A1A1A; }
  }
`;

const THEME_OPTIONS = [
  { id: '', label: 'Bitte wählen...' },
  // Classic und Modern fehlten: Wer aus diesen Demos kam (oder sie im
  // Filmstrip anfragte), landete ohne Design-Vorauswahl im Formular, und die
  // Anfrage enthielt kein Design.
  { id: 'classic', label: 'Classic' },
  { id: 'editorial', label: 'Editorial' },
  { id: 'botanical', label: 'Botanical' },
  { id: 'contemporary', label: 'Contemporary' },
  { id: 'luxe', label: 'Luxe' },
  { id: 'neon', label: 'Neon' },
  { id: 'video', label: 'Video' },
  { id: 'modern', label: 'Modern' },
];

const ContactSection = () => {
  const { currentTheme } = useTheme();
  const config = THEME_CONFIG[currentTheme] || THEME_CONFIG.video;
  const { partnerRef, couponCode: partnerCoupon } = usePartnerRef();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    weddingDate: '',
    interestedTheme: '',
    interestedPackage: '',
    message: '',
    couponCode: '',
    honeypot: '', // Spam trap
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Optionale Felder (Telefon, Datum, Theme, Paket, Gutschein) sind eingeklappt,
  // um die sichtbare Formular-Hürde zu senken. State + Submit-Payload unverändert.
  const [showOptional, setShowOptional] = useState(false);
  // Sichtbare Bestätigung: "Ausgewählter Stil: Editorial"
  const [selectedStyle, setSelectedStyle] = useState(null);

  // Quick-Select gegen die Schreibblockade: füllt die Pflicht-Nachricht mit einem Startsatz
  const applyMessageChip = (text) => {
    setFormData(prev => ({
      ...prev,
      message: prev.message ? prev.message : text,
    }));
  };
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  
  // hCaptcha state
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const captchaWidgetId = useRef(null);
  
  // Timing-based spam protection
  const formLoadTime = useRef(Date.now());
  const MIN_SUBMIT_TIME = 3000; // 3 seconds minimum
  const sectionRef = useRef(null);

  // Listen for package selection from PricingSection
  useEffect(() => {
    const handleSelectPackage = (e) => {
      const pkgId = e.detail;
      if (pkgId && PACKAGES.find(p => p.id === pkgId)) {
        setFormData(prev => ({ ...prev, interestedPackage: pkgId }));
        setShowOptional(true); // Vorauswahl für den Nutzer sichtbar machen
      }
    };
    window.addEventListener('selectPackage', handleSelectPackage);

    // Also check URL hash on mount (e.g. #contact?package=starter)
    const hash = window.location.hash;
    if (hash.includes('package=')) {
      const match = hash.match(/package=([a-z]+)/);
      if (match && PACKAGES.find(p => p.id === match[1])) {
        setFormData(prev => ({ ...prev, interestedPackage: match[1] }));
      }
    }

    return () => window.removeEventListener('selectPackage', handleSelectPackage);
  }, []);

  // Stil-Vorauswahl: "Diesen Stil anfragen" aus Filmstrip, Demo-Rückkehr
  // (/#contact?theme=editorial) oder Session (Umweg über siwedding.de).
  //
  // WICHTIG: Früher wurde hier currentTheme vorbelegt. Seit die Marketing-
  // Seite fest im Classic-Theme läuft, stand in JEDER Anfrage "Classic" —
  // unabhängig davon, welche Demo das Paar angesehen hatte.
  const applyTheme = useCallback((themeId) => {
    if (!themeId || !THEME_OPTIONS.find(t => t.id === themeId)) return;
    setFormData(prev => ({ ...prev, interestedTheme: themeId }));
    setSelectedStyle(themeId);
    setShowOptional(true); // Vorauswahl sichtbar machen
  }, []);

  useEffect(() => {
    const handleSelectTheme = (e) => applyTheme(e.detail);
    window.addEventListener('selectTheme', handleSelectTheme);

    // 1. Hash-Parameter (teilbarer Link, auch als Rückweg von der Demo)
    const match = window.location.hash.match(/theme=([a-z]+)/);
    if (match) {
      applyTheme(match[1]);
    } else {
      // 2. Session — überlebt den Umweg über siwedding.de
      const stored = getStyleChoice();
      if (stored?.theme) applyTheme(stored.theme);
    }

    return () => window.removeEventListener('selectTheme', handleSelectTheme);
  }, [applyTheme]);

  // Pre-fill coupon code from partner referral
  useEffect(() => {
    if (partnerCoupon) {
      setFormData(prev => prev.couponCode ? prev : { ...prev, couponCode: partnerCoupon });
      setShowOptional(true); // Gutschein sichtbar machen
    }
  }, [partnerCoupon]);

  // --- hCaptcha lazy loading: only load when user interacts with form ---
  const captchaLoading = useRef(false);

  const loadCaptchaScript = useCallback(() => {
    if (captchaLoading.current || document.querySelector('script[src*="hcaptcha"]')) return;
    captchaLoading.current = true;

    const script = document.createElement('script');
    script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Script loaded → render widget
      if (!captchaRef.current || captchaWidgetId.current !== null || !window.hcaptcha) return;
      try {
        captchaWidgetId.current = window.hcaptcha.render(captchaRef.current, {
          sitekey: HCAPTCHA_SITE_KEY,
          theme: ['editorial', 'botanical', 'luxe', 'neon', 'video'].includes(currentTheme) ? 'dark' : 'light',
          size: 'normal',
          callback: (token) => setCaptchaToken(token),
          'expired-callback': () => setCaptchaToken(null),
          'error-callback': () => setCaptchaToken(null),
        });
      } catch (e) { /* Widget already rendered */ }
    };
    document.head.appendChild(script);
  }, [currentTheme]);

  // Trigger hCaptcha load on first form interaction (focus, click, touch)
  // Erste Interaktion mit dem Formular = Anfrage gestartet.
  // form_start war bisher zwar definiert, wurde aber nirgends gefeuert —
  // deshalb stand im Report dauerhaft "Formular gestartet: 0".
  const formStarted = useRef(false);
  const handleFormInteraction = useCallback(() => {
    loadCaptchaScript();
    if (!formStarted.current) {
      formStarted.current = true;
      trackFormStart();
    }
  }, [loadCaptchaScript]);

  // Also render captcha if script was already loaded (e.g. navigated back)
  useEffect(() => {
    if (window.hcaptcha && captchaRef.current && captchaWidgetId.current === null) {
      try {
        captchaWidgetId.current = window.hcaptcha.render(captchaRef.current, {
          sitekey: HCAPTCHA_SITE_KEY,
          theme: ['editorial', 'botanical', 'luxe', 'neon', 'video'].includes(currentTheme) ? 'dark' : 'light',
          size: 'normal',
          callback: (token) => setCaptchaToken(token),
          'expired-callback': () => setCaptchaToken(null),
          'error-callback': () => setCaptchaToken(null),
        });
      } catch (e) { /* already rendered */ }
    }
  }, [currentTheme]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    // Spam Check 1: Honeypot
    if (formData.honeypot) {
      setSubmitStatus('success'); // Fake success for bots
      return;
    }
    
    // Spam Check 2: Time-based (too fast = bot)
    const timeTaken = Date.now() - formLoadTime.current;
    if (timeTaken < MIN_SUBMIT_TIME) {
      setSubmitStatus('success'); // Fake success for bots
      return;
    }
    
    // Spam Check 3: hCaptcha
    if (!captchaToken) {
      setErrorMessage('Bitte bestätige, dass du kein Roboter bist.');
      return;
    }
    
    // Client-side validation
    if (!formData.name.trim()) {
      setErrorMessage('Bitte gib deinen Namen ein.');
      return;
    }
    if (!validateEmail(formData.email)) {
      setErrorMessage('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }
    if (!formData.message.trim()) {
      setErrorMessage('Bitte schreibe uns eine Nachricht.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Einziger Call: an unsere eigene Serverless Function
      // Kein Brevo-Key, kein Supabase-Key im Frontend!
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim(),
          weddingDate: formData.weddingDate,
          interestedTheme: formData.interestedTheme,
          interestedPackage: formData.interestedPackage,
          message: formData.message.trim(),
          couponCode: formData.couponCode.trim() || null,
          partnerRef: partnerRef || null,
          captchaToken,
        }),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Ein Fehler ist aufgetreten.');
      }
      
      setSubmitStatus('success');
      
      // theme = vom Paar gewähltes Design (nicht das Seiten-Theme der
      // Marketingseite — das ist fest "classic" und stand vorher in JEDEM Lead)
      trackFormSubmit(formData.interestedTheme || 'none', formData.interestedPackage);
      // Funnel-Abschluss inkl. Herkunftsartikel (si_blog_origin aus der Session)
      trackInquirySubmit(formData.interestedTheme || 'none', formData.interestedPackage);
      
      // Reset captcha
      if (window.hcaptcha && captchaWidgetId.current !== null) {
        window.hcaptcha.reset(captchaWidgetId.current);
      }
      setCaptchaToken(null);
      
    } catch (error) {
      console.error('Contact form error:', error);
      setErrorMessage(error.message || 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Theme-specific content
  const getContent = () => {
    switch (currentTheme) {
      case 'contemporary':
        return {
          eyebrow: '💌 Der erste Schritt',
          title: 'Erzählt uns von euch',
          subtitle: 'Kein Verkaufsgespräch. Nur ein ehrliches Kennenlernen.',
          button: 'Nachricht schreiben →',
        };
      case 'neon':
        return {
          eyebrow: '// contact.init(your_story)',
          title: 'Erzählt uns von euch',
          subtitle: 'Keine Bots. Keine Formulare. Echte Menschen.',
          button: 'Message.send() →',
        };
      case 'luxe':
        return {
          eyebrow: 'Der erste Schritt',
          title: 'Erzählt uns von euch',
          subtitle: 'Wir melden uns persönlich — versprochen.',
          button: 'Nachricht schreiben',
        };
      default:
        return {
          eyebrow: 'Der erste Schritt',
          title: 'Erzählt uns von euch',
          subtitle: 'Ihr plant eure Hochzeit und möchtet eine Website, die wirklich zu euch passt? Erzählt uns kurz von euch — wir melden uns persönlich.',
          button: 'Anfrage senden →',
        };
    }
  };
  
  const content = getContent();

  // Shared form JSX (used by both default and classic)
  const formContent = submitStatus === 'success' ? (
    <SuccessMessage $theme={currentTheme} $config={config}>
      <h3>✓ Vielen Dank!</h3>
      <p>
        Wir haben eure Anfrage erhalten und melden uns
        <span className="highlight"> innerhalb von 24 Stunden</span> bei euch.
      </p>
      <p>
        Wir freuen uns auf das Gespräch!
      </p>
      {/* Wartezeit sinnvoll nutzen: zurück in die Designwelten */}
      <p style={{ marginTop: '1.25rem' }}>
        Ihr könnt euch inzwischen schon einmal durch unsere Designwelten klicken.
      </p>
      <p style={{ marginTop: '0.75rem' }}>
        <a
          href="#themes"
          style={{ color: config.accent, fontWeight: 600 }}
          onClick={(e) => {
            e.preventDefault();
            document.getElementById('themes')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Designs ansehen →
        </a>
      </p>
      <p style={{ marginTop: '1.5rem', fontSize: '0.85rem' }}>
        Fragen? Schreibt uns direkt an{' '}
        <a href="mailto:wedding@sarahiver.de" style={{ color: config.accent }}>
          wedding@sarahiver.de
        </a>
      </p>
    </SuccessMessage>
  ) : (
    <Form onSubmit={handleSubmit} onFocus={handleFormInteraction} onClick={handleFormInteraction} onTouchStart={handleFormInteraction}>
      {/* Kommt das Paar aus einer Demo, ist der Stil schon gesetzt —
          sichtbar bestätigen statt still im eingeklappten Feld verstecken. */}
      {selectedStyle && (
        <StyleBadge>
          <span>Ausgewählter Stil: <strong>{THEME_OPTIONS.find(t => t.id === selectedStyle)?.label || selectedStyle}</strong></span>
          <button
            type="button"
            onClick={() => {
              setSelectedStyle(null);
              setFormData(prev => ({ ...prev, interestedTheme: '' }));
            }}
          >
            ändern
          </button>
        </StyleBadge>
      )}
      <Honeypot
        type="text"
        name="honeypot"
        value={formData.honeypot}
        onChange={handleChange}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        aria-label="Nicht ausfüllen"
      />

      <FormRow>
        <FormGroup>
          <Label htmlFor="contact-name" $theme={currentTheme} $config={config}>Name *</Label>
          <Input
            id="contact-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Euer Name"
            required
            $theme={currentTheme}
            $config={config}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="contact-email" $theme={currentTheme} $config={config}>E-Mail *</Label>
          <Input
            id="contact-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="email@beispiel.de"
            required
            $theme={currentTheme}
            $config={config}
          />
        </FormGroup>
      </FormRow>

      <OptionalToggle
        type="button"
        $config={config}
        onClick={() => setShowOptional(v => !v)}
        aria-expanded={showOptional}
      >
        {showOptional ? '− Weniger Angaben' : '+ Mehr Angaben (Telefon, Datum, Theme, Paket) — optional'}
      </OptionalToggle>

      <OptionalFields $open={showOptional}>
      <FormRow>
        <FormGroup>
          <Label htmlFor="contact-phone" $theme={currentTheme} $config={config}>Telefon</Label>
          <Input
            id="contact-phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+49 123 456789"
            $theme={currentTheme}
            $config={config}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="contact-weddingDate" $theme={currentTheme} $config={config}>Hochzeitsdatum</Label>
          <Input
            id="contact-weddingDate"
            type="date"
            name="weddingDate"
            value={formData.weddingDate}
            onChange={handleChange}
            $theme={currentTheme}
            $config={config}
          />
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <Label htmlFor="contact-theme" $theme={currentTheme} $config={config}>Euer Stil</Label>
          <Select
            id="contact-theme"
            name="interestedTheme"
            value={formData.interestedTheme}
            onChange={handleChange}
            $theme={currentTheme}
            $config={config}
          >
            {THEME_OPTIONS.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="contact-package" $theme={currentTheme} $config={config}>Interesse an Paket</Label>
          <Select
            id="contact-package"
            name="interestedPackage"
            value={formData.interestedPackage}
            onChange={handleChange}
            $theme={currentTheme}
            $config={config}
          >
            {PACKAGES.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </Select>
        </FormGroup>
      </FormRow>

      {/* Gutscheincode — vorausgefüllt bei Partner-Referral, sonst optional */}
      <FormGroup>
        <Label htmlFor="contact-coupon" $theme={currentTheme} $config={config}>Gutscheincode</Label>
        <Input
          id="contact-coupon"
          type="text"
          name="couponCode"
          value={formData.couponCode}
          onChange={handleChange}
          placeholder=""
          $theme={currentTheme}
          $config={config}
          style={partnerCoupon && formData.couponCode === partnerCoupon ? { opacity: 0.8 } : {}}
        />
      </FormGroup>
      </OptionalFields>

      <FormGroup>
        <Label htmlFor="contact-message" $theme={currentTheme} $config={config}>Nachricht *</Label>
        <ChipRow>
          <Chip type="button" $config={config} onClick={() => applyMessageChip('Wir heiraten und suchen eine Hochzeitswebsite. Erzählt uns mehr!')}>Wir heiraten bald</Chip>
          <Chip type="button" $config={config} onClick={() => applyMessageChip('Wir schauen uns gerade um und hätten gern mehr Infos zu euren Paketen.')}>Erstmal nur schauen</Chip>
          <Chip type="button" $config={config} onClick={() => applyMessageChip('Uns gefällt eine eurer Demos — wie läuft der Ablauf ab?')}>Demo gefällt uns</Chip>
        </ChipRow>
        <Textarea
          id="contact-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Erzählt uns von eurer Hochzeit..."
          required
          $theme={currentTheme}
          $config={config}
        />
      </FormGroup>

      {errorMessage && (
        <ErrorMessage $config={config}>{errorMessage}</ErrorMessage>
      )}

      <CaptchaWrapper>
        <div ref={captchaRef}></div>
      </CaptchaWrapper>

      {/* Hürde senken: sagt vor dem Klick, was danach passiert (§27) */}
      <PrivacyNote $config={config} style={{ marginBottom: '0.9rem', textAlign: 'center' }}>
        Eure Anfrage ist unverbindlich. Wir melden uns persönlich bei euch.
      </PrivacyNote>
      <Button
        type="submit"
        disabled={isSubmitting}
        $theme={currentTheme}
        $config={config}
      >
        {isSubmitting ? 'Wird gesendet...' : content.button}
      </Button>

      <PrivacyNote $config={config}>
        Mit dem Absenden stimmst du unserer{' '}
        <a href="/datenschutz">Datenschutzerklärung</a> zu.{' '}
        Wir melden uns innerhalb von 24 Stunden.
      </PrivacyNote>
    </Form>
  );

  // ── Classic: sticky photo left, form right ──
  if (currentTheme === 'classic') {
    return (
      <ClassicSection id="contact" ref={sectionRef}>
        <ClassicGrid>
          <ClassicImageCol>
            <ClassicImage src={CLASSIC_CONTACT_IMAGE} alt="Brautpaar – Kontakt aufnehmen für eure Hochzeitswebsite" loading="lazy" />
          </ClassicImageCol>
          <ClassicFormCol>
            <ClassicHeader>
              <ClassicEyebrow>Der erste Schritt</ClassicEyebrow>
              <ClassicTitle>Erzählt uns von euch</ClassicTitle>
              <ClassicSubtitle>
                Kein Verkaufsgespräch. Nur ein ehrliches Kennenlernen. Ihr schreibt direkt an Sarah &amp; Iver.
              </ClassicSubtitle>
            </ClassicHeader>
            {formContent}
          </ClassicFormCol>
        </ClassicGrid>
      </ClassicSection>
    );
  }

  // ── All other themes: centered layout ──
  return (
    <Section id="contact" ref={sectionRef} $theme={currentTheme} $config={config}>
      <Container>
        <Header>
          <Eyebrow $theme={currentTheme} $config={config}>{content.eyebrow}</Eyebrow>
          <Title $theme={currentTheme} $config={config}>{content.title}</Title>
          <Subtitle $theme={currentTheme} $config={config}>{content.subtitle}</Subtitle>
        </Header>

        <FormCard $theme={currentTheme} $config={config}>
          {formContent}
        </FormCard>
      </Container>
    </Section>
  );
};

export default ContactSection;
